import { useState, useRef, useEffect, useCallback } from "react";
import { flushSync, createPortal } from "react-dom";
import { useLocation, useNavigate  } from "react-router-dom";
import { createMindARVolcanSession } from "../lib/createMindARVolcanSession";
import "./ARView.css";
import "./ARIntro.css";

const IDS = {
  container: "ar-mindar-container",
  footer: "ar-footer-text",
  pinOverlay: "ar-pin-overlay",
  pinMessage: "ar-pin-message",
  reload: "ar-reload-btn",
};

function startARFromClick() {
  const container = document.getElementById(IDS.container);
  const footer = document.getElementById(IDS.footer);
  const pinOverlay = document.getElementById(IDS.pinOverlay);
  const pinMessage = document.getElementById(IDS.pinMessage);
  const reloadBtn = document.getElementById(IDS.reload);

  if (container && footer && pinOverlay && pinMessage && reloadBtn) {
    return {
      container,
      footer,
      pinOverlay,
      pinMessage,
      reloadBtn,
    };
  }
  return null;
}

function ARIntro({ onContinue, onBackHome }) {
  return (
    <div className="ar-intro">
      <div className="ar-intro-inner">
        <h1>Cámara para la experiencia RA</h1>
        <p>
          Usamos la cámara para reconocer las tarjetas del libro y mostrar los
          volcanes en Realidad Aumentada. Solo la usamos para eso; no guardamos
          fotos ni videos.
        </p>
        <div className="ar-intro-actions">
          <button type="button" className="btn-primary" onClick={onContinue}>
            Permitir y continuar
          </button>
          <button type="button" className="btn-ghost" onClick={onBackHome}>
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
}

/** Vista RA: solo marcado; la sesión MindAR se crea en el manejador del clic (mismo gesto de usuario). */
function ARActiveView({ showStartOverlay, onStartCamera, onBackHome }) {
  /** Portal a body: el canvas WebGL de MindAR a veces queda encima del árbol de #root y roba los toques. */
  const chromeLayer = (
    <div className="ar-ui-chrome">
      <button
        type="button"
        className="ar-back"
        onClick={(e) => {
          e.stopPropagation();
          onBackHome();
        }}
      >
        ← Inicio
      </button>
      <button
        type="button"
        id={IDS.reload}
        className="ar-reload-btn"
        aria-label="Reiniciar cámara"
      >
        <img src={`${process.env.PUBLIC_URL}/reload.png`} alt="" />
      </button>
    </div>
  );

  return (
    <div className="ar-page-root">
      {showStartOverlay && (
        <div
          className="ar-start-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ar-start-title"
        >
          <div className="ar-start-overlay-inner">
            <h2 id="ar-start-title" className="ar-start-title">
            Acceso a cámara para experiencia AR
            </h2>
            <p className="ar-start-text">
              Usamos la cámara para reconocer las tarjetas del libro y mostrar los
            volcanes en Realidad Aumentada. Solo la usamos para eso; no guardamos
            fotos ni videos.
            </p>
            <button
              type="button"
              className="ar-start-btn"
              onClick={onStartCamera}
            >
              Iniciar cámara
            </button>
          </div>
        </div>
      )}
      {typeof document !== "undefined"
        ? createPortal(chromeLayer, document.body)
        : null}
      <div id={IDS.container} className="ar-mindar-container">
        <div id={IDS.pinOverlay} className="pin-container">
          <div id="pin-ar" role="button" tabIndex={0}>
            <div className="pin-dot">
              <img src={`${process.env.PUBLIC_URL}/pin.png`} alt="" />
            </div>
            <div className="pin-pulse" aria-hidden />
          </div>
          <div id={IDS.pinMessage} className="pin-message hide">
            <p>Este volcán tiene 3.000 m de altura</p>
          </div>
        </div>
      </div>
      <div id={IDS.footer} className="ar-footer-text" />
    </div>
  );
}

export default function ARPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const skipIntroFromLanding = location.state?.skipIntro === true;

  const [step, setStep] = useState(() =>
    skipIntroFromLanding ? "ar-ready" : "intro"
  );
  const sessionRef = useRef(null);

  /** Recarga completa del home (estado limpio, cámara liberada del todo en la nueva página). */
  const handleLeaveAR = useCallback(() => {
    sessionRef.current?.dispose();
    sessionRef.current = null;
    const base = process.env.PUBLIC_URL || "/";
    const home = base === "/" ? "/" : `${String(base).replace(/\/$/, "")}/`;
    window.location.assign(home);
  }, []);

  /** Cierra cámara y WebGL de MindAR antes de cambiar de ruta (evita pantalla en negro / mapa sin interacción). */
  const navigateFromAR = useCallback(
    (to, options) => {
      sessionRef.current?.dispose();
      sessionRef.current = null;
      // Un fotograma después de cerrar la cámara/WebGL mejora el arranque del siguiente canvas (móvil).
      requestAnimationFrame(() => navigate(to, options));
    },
    [navigate]
  );

  const startSessionIfReady = useCallback(() => {
    const runSession = () => {
      const els = startARFromClick();
      if (!els) return false;
      sessionRef.current?.dispose();
      sessionRef.current = null;
      const session = createMindARVolcanSession(els, navigateFromAR);
      sessionRef.current = session;
      void session.start();
      return true;
    };

    if (!runSession()) {
      requestAnimationFrame(() => {
        if (!runSession()) {
          requestAnimationFrame(runSession);
        }
      });
    }
  }, [navigateFromAR]);

  useEffect(() => {
    return () => {
      sessionRef.current?.dispose();
      sessionRef.current = null;
    };
  }, []);

  const beginARSession = useCallback(() => {
    flushSync(() => setStep("ar"));
    startSessionIfReady();
  }, [startSessionIfReady]);

  if (step === "intro") {
    return (
      <ARIntro onContinue={beginARSession} onBackHome={handleLeaveAR} />
    );
  }

  return (
    <ARActiveView
      showStartOverlay={step === "ar-ready"}
      onStartCamera={beginARSession}
      onBackHome={handleLeaveAR}
    />
  );
}
